import React, { useCallback, useMemo, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import SearchBar from 'flow/components/SearchBar';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import useCustomer from 'flow/hooks/useCustomer';
import { canInviteColleagues } from 'flow/selectors/customer-selectors';
import {
    ContactKey,
    ExtensionKey,
    ThirdPartyContactKey,
} from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import {
    isContactKey,
    isExtensionKey,
    isThirdPartyContactKey,
} from 'flow/utils';
import { setRecentColleague } from 'flow/utils/browser-utils';
import { getCountryCode } from 'flow/utils/extension-utils';
import ActionMenu from './ActionMenu';
import ContactList from './contact-list/ContactList';
import { CENTRAL_SWITCH_OVERVIEW_KEY } from './contact-list/contact-list-rows';
import { useAppSelector } from 'flow/hooks/useRedux';

interface Props {
    selected:
        | ExtensionKey
        | ContactKey
        | ThirdPartyContactKey
        | typeof CENTRAL_SWITCH_OVERVIEW_KEY;
}

const ExtensionListTab = ({ selected }: Props) => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { openModal } = useModal();

    const { ownCustomer } = useCustomer();
    const { ownContact } = useContact();

    const presentInviteColleaguesOption = useAppSelector(canInviteColleagues);

    const [extensionFilter, setExtensionFilter] = useState('');

    const navigateOnItemSelected = useCallback(
        (key: string) => {
            if (isExtensionKey(key)) {
                history.push(
                    generatePath(routes['extensions.entityKey.contact'], {
                        entityKey: key,
                    }),
                );
                setRecentColleague(key);
            } else if (isContactKey(key)) {
                history.push(
                    generatePath(routes['extensions.contact.contactKey'], {
                        contactKey: key,
                    }),
                );
            } else if (isThirdPartyContactKey(key)) {
                history.push(
                    generatePath(
                        routes[
                            'extensions.thirdpartycontact.thirdPartyContactKey'
                        ],
                        {
                            thirdPartyContactKey: key,
                        },
                    ),
                );
            } else if (key === CENTRAL_SWITCH_OVERVIEW_KEY) {
                history.push(routes['extensions.overview']);
            }
        },
        [history],
    );

    const actions = useMemo(() => {
        return presentInviteColleaguesOption
            ? [
                  {
                      icon: 'create_contact',
                      name: i18next.t('Colleagues.Create.External.Title'),
                      click: () => handleCreateContact(),
                  },
                  {
                      icon: 'account',
                      name: i18next.t('Colleagues.Invite.External.Title'),
                      click: () => openModal('inviteColleague'),
                  },
              ]
            : [
                  {
                      icon: 'create_contact',
                      name: i18next.t('Colleagues.Create.External.Title'),
                      click: () => handleCreateContact(),
                  },
              ];
    }, [presentInviteColleaguesOption]);

    const handleCreateContact = () => {
        openModal('createContact', {
            countryCode: getCountryCode(ownContact, ownCustomer),
        });
    };

    return (
        <div className="flex grow flex-col border-e">
            <div className="p-3">
                <SearchBar
                    onChange={setExtensionFilter}
                    initialSearchText={extensionFilter}
                    enableShortcut={true}
                >
                    {/*
                     * With one action the menu is that action, so it borrows
                     * the action's own name. With both, the name says what
                     * the two actions do.
                     */}
                    <ActionMenu
                        actions={actions}
                        triggerLabel={
                            actions.length === 1
                                ? actions[0].name
                                : i18next.t('Colleagues.AddOrInvite.Title')
                        }
                    />
                </SearchBar>
            </div>

            <div className="h-full ps-3 pb-3">
                <ContactList
                    selected={selected}
                    onItemSelected={navigateOnItemSelected}
                    extensionFilter={extensionFilter}
                />
            </div>
        </div>
    );
};

export default React.memo(ExtensionListTab);
